import 'styles/cart/cart-upsell-banner.scss';

import React from 'react';
import classNames from 'classnames';

import { TranslatedMessage } from 'common/components/Intl';
import propTypes from 'propTypes';
import {
  SHIPPING_COST_THRESHOLD,
  SMOKER_REFERAL_DISCOUNT_CENTS,
} from 'apps/cart/appConstants';
import PriceSpan from 'common/components/formatters/PriceSpan';
import MembershipToggle from 'containers/MembershipToggle';
import * as paths from '../../paths';
import api from 'api/ApiClient';

const TYPE_MEMBERSHIP = 'membership';
const TYPE_COMMITMENT = 'commitment';
const TYPE_SHIPPING = 'shipping';
const TYPE_REFERRAL = 'referral';

export default class UpsellBanner extends React.Component {
  static propTypes = {
    isMembershipOrder: propTypes.bool.isRequired,
    subscribableQuantity: propTypes.number.isRequired,
    requiredSubscribableItems: propTypes.number.isRequired,
    minimumSubscriptionItems: propTypes.number.isRequired,
    hasSubscriptionCommitment: propTypes.bool.isRequired,
    canBecomeMember: propTypes.bool.isRequired,
    shippingCost: propTypes.number.isRequired,
    isSubscribableStore: propTypes.bool.isRequired,
  };

  get type() {
    const {
      subscribableQuantity,
      canBecomeMember,
      hasSubscriptionCommitment,
      isSubscribableStore,
    } = this.props;
    if (
      hasSubscriptionCommitment &&
      !this.hasMinimumSubscribableItems &&
      isSubscribableStore
    ) {
      return TYPE_COMMITMENT;
    } else if (
      canBecomeMember &&
      subscribableQuantity > 0 &&
      isSubscribableStore
    ) {
      return TYPE_MEMBERSHIP;
    } else if (
      api.splitTreatments &&
      api.splitTreatments.upsell_banner_referral_program &&
      this.hasFreeShipping
    ) {
      return TYPE_REFERRAL;
    }
    return TYPE_SHIPPING;
  }

  get text() {
    switch (this.type) {
      case TYPE_MEMBERSHIP:
        return this.textMembership;
      case TYPE_COMMITMENT:
        return <TranslatedMessage id={'cart.upsell_banner.join_auto_ship'} />;
      case TYPE_REFERRAL:
        return (
          <TranslatedMessage id={'cart.upsell_banner.get_free_shipping'} />
        );
      case TYPE_SHIPPING:
        return <TranslatedMessage id={'cart.upsell_banner.free_shipping'} />;
    }
  }

  get textMembership() {
    const {
      isMembershipOrder,
      subscribableQuantity,
      minimumSubscriptionItems,
      subscribableVariantDiscountRate,
      subscribableVariantFreeFrequency,
    } = this.props;
    const needsMoreSubscribableItems =
      subscribableQuantity < minimumSubscriptionItems;
    if (needsMoreSubscribableItems && subscribableVariantDiscountRate) {
      return (
        <TranslatedMessage
          id={'cart.upsell_banner.save_15_with_subscription'}
          values={{
            subscribableVariantDiscountRate:
              subscribableVariantDiscountRate * 100,
          }}
        />
      );
    } else if (needsMoreSubscribableItems && subscribableVariantFreeFrequency) {
      return (
        <TranslatedMessage
          id={'cart.upsell_banner.buy_x_get_one_with_subscription'}
          values={{ subscribableVariantFreeFrequency }}
        />
      );
    } else if (isMembershipOrder) {
      return (
        <TranslatedMessage id={'cart.upsell_banner.subscribe_to_shipping'} />
      );
    }
    return <TranslatedMessage id={'cart.upsell_banner.join_auto_ship'} />;
  }

  get hasMinimumSubscribableItems() {
    return this.props.requiredSubscribableItems <= 0;
  }

  get hasFreeShipping() {
    return this.props.shippingCost <= 0;
  }

  get header() {
    switch (this.type) {
      case TYPE_MEMBERSHIP:
        return this.headerMembership;
      case TYPE_COMMITMENT:
        return this.headerCommitment;
      case TYPE_REFERRAL:
        return this.headerReferral;
      case TYPE_SHIPPING:
        return (
          <TranslatedMessage
            id={'cart.upsell_banner.orders_over_free_shipping'}
            values={{
              price: <PriceSpan cents={SHIPPING_COST_THRESHOLD} />,
            }}
          />
        );
    }
  }

  get headerCommitment() {
    return (
      <div className="toupe">
        {this.insufficientSubscribableItemsText}
        <TranslatedMessage
          id={'cart.upsell_banner.exclusive_device_promotion'}
        />
      </div>
    );
  }

  get headerReferral() {
    return (
      <TranslatedMessage
        id={'cart.upsell_banner.refer_smoker'}
        values={{
          referral_discount: (
            <PriceSpan cents={SMOKER_REFERAL_DISCOUNT_CENTS} />
          ),
        }}
      />
    );
  }

  get headerMembership() {
    if (this.hasMinimumSubscribableItems) {
      return this.renderUpsellText;
    }

    return this.insufficientSubscribableItemsText;
  }

  get renderUpsellText() {
    const {
      subscribableVariantDiscountRate,
      subscribableVariantFreeFrequency,
    } = this.props;

    if (subscribableVariantDiscountRate) {
      return (
        <TranslatedMessage
          id={'cart.upsell_banner.save_15'}
          values={{
            subscribableVariantDiscountRate:
              subscribableVariantDiscountRate * 100,
          }}
        />
      );
    } else if (subscribableVariantFreeFrequency) {
      return (
        <div>
          <TranslatedMessage
            id={'cart.upsell_banner.buy_x_juulpod_packs'}
            values={{ subscribableVariantFreeFrequency }}
          />
          <br />
          <TranslatedMessage id={'cart.upsell_banner.get_one_with_autoship'} />
        </div>
      );
    }
  }

  get insufficientSubscribableItemsText() {
    return (
      <TranslatedMessage
        id={'cart.upsell_banner.insufficient_items'}
        values={{ count: this.props.requiredSubscribableItems }}
      />
    );
  }

  renderDisplayHeader() {
    const { type } = this;
    const { requiredSubscribableItems } = this.props;
    const classHeader = classNames(
      'CartUpsellBanner__header',
      type === TYPE_MEMBERSHIP ? 'toupe' : 'flat-blue'
    );
    if (requiredSubscribableItems > 0) {
      return (
        <div>
          <a className={classHeader} href={paths.shopPods()}>
            {this.header}
          </a>
        </div>
      );
    }
    return <h5 className={classHeader}>{this.header}</h5>;
  }

  render() {
    const { type } = this;

    const { hasSubscriptionCommitment, canBecomeMember } = this.props;

    const className = `CartUpsellBanner CartUpsellBanner--${type}`;

    const isShowingMembershipToggle =
      type === TYPE_MEMBERSHIP && canBecomeMember && !hasSubscriptionCommitment;

    return (
      <div className={className}>
        <div className="CartUpsellBanner__text">
          <span className="navy label">{this.text}</span>
          {this.renderDisplayHeader()}
          {type === TYPE_MEMBERSHIP && (
            <a
              className="text--small CartUpsellBanner__link"
              onClick={this.props.openLearnMoreModal}
            >
              <TranslatedMessage id={'cart.upsell_banner.learn_more'} />
            </a>
          )}
        </div>
        {isShowingMembershipToggle && <MembershipToggle />}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/javascript/apps/cart/components/sidebar/UpsellBanner.js